import { fileHref } from '../lib/fileAccess'
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { DRILL_TYPES, DRILL_BANDS, DRILL_SCHEDULE, drillsNeedingAttention, openPlannedDrills } from '../lib/drillTypes'
import { shipTodayAuto } from '../lib/shipTime'
import DrillPlansPanel, { useDrillPlans } from '../components/DrillPlansPanel'
import { linkFor, requestDrill, drillTypesOf } from '../lib/drillPlans'
import { whoOf } from '../components/EmergencyLauncher'
import { useLinkedDocs, DocChips } from '../components/SopLinks'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function DrillMatrix({ onNavigate, initialFilter = '' }) {
  const sopLinks = useLinkedDocs('drill')   // SOPs / manuals linked to a drill (sql/sop-layer.sql)
  const { config, can } = useAuth()
  const plansData = useDrillPlans(config?.id)          // Contingency Plans, drill links, drill runs (src/components/DrillPlansPanel.jsx)
  const [records, setRecords] = useState([])
  const [year, setYear] = useState(new Date().getFullYear())
  const [view, setView] = useState(initialFilter === 'plans' ? 'plans' : initialFilter ? 'table' : 'grid') // 'grid' | 'table' | 'plans'
  const [tab, setTab] = useState(initialFilter || 'all')   // all | overdue | due | completed
  const [search, setSearch] = useState('')
  const [freqFilter, setFreqFilter] = useState('all')
  const [recordsFor, setRecordsFor] = useState(null)
  const [cellMenu, setCellMenu] = useState(null) // { label, count, x, y }
  const [loading, setLoading] = useState(true)
  const [planned, setPlanned] = useState([])   // drill_due_dates (sql/audit-fixes.sql): a due date per drill type, not a record
  const [planForm, setPlanForm] = useState(null) // { drill_type, due_date } while planning a drill here
  const [planErr, setPlanErr] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await supabase.from('drill_records').select('*').eq('client_id', config.id)
      setRecords(data || [])
      // planned drills: none until sql/audit-fixes.sql is run (the read fails quietly)
      const { data: pd } = await supabase.from('drill_due_dates').select('id,drill_type,due_date,notes,planned_by,created_at').eq('client_id', config.id)
      setPlanned(pd || [])
    } catch (e) { /* ignore */ }
    setLoading(false)
  }, [config])

  useEffect(() => { if (config?.id) load() }, [config, load])

  // month index (0-11) where a given drill was done in the selected year -> Set
  function doneMonths(label) {
    const set = new Set()
    records.forEach(r => {
      if (r.drill_type !== label) return
      const d = new Date(r.drill_date + 'T00:00:00')
      if (d.getFullYear() === year) set.add(d.getMonth())
    })
    return set
  }

  const now = new Date()
  const curYear = now.getFullYear()
  const curMonth = now.getMonth()

  const openPlans = openPlannedDrills(planned, records, shipTodayAuto())
  const planIn = (label, monthIdx) => openPlans.find(p => p.drill_type === label && Number(p.due_date.slice(0, 4)) === year && Number(p.due_date.slice(5, 7)) - 1 === monthIdx)
  const canPlan = !can || can('lsa', 'write')
  async function savePlan() {
    setPlanErr('')
    const f = planForm || {}
    if (!f.drill_type || !/^\d{4}-\d{2}-\d{2}$/.test(f.due_date || '')) { setPlanErr('Pick a drill and a due date.'); return }
    if (f.due_date < shipTodayAuto()) { setPlanErr('That day has passed - log the drill if it was held.'); return }
    const have = planned.find(p => p.drill_type === f.drill_type)
    const row = { drill_type: f.drill_type, due_date: f.due_date, planned_by: whoOf(config) || null }
    const res = have ? await supabase.from('drill_due_dates').update(row).eq('id', have.id) : await supabase.from('drill_due_dates').insert({ client_id: config.id, ...row })
    if (res.error) { setPlanErr('Not saved: ' + res.error.message + (/drill_due_dates|schema cache|does not exist/i.test(res.error.message) ? ' (sql/audit-fixes.sql is not run yet)' : '')); return }
    setPlanForm(null); load()
  }

  // cell status: 'done' | 'due' | 'overdue' | 'planned' | 'na'
  function cellStatus(drill, monthIdx, doneSet) {
    if (doneSet.has(monthIdx)) return 'done'
    if (planIn(drill.label, monthIdx)) return 'planned'
    const sched = DRILL_SCHEDULE[drill.key] || []
    if (!sched.includes(monthIdx)) return 'na'
    // Current month on a scheduled slot: live status
    if (year === curYear && monthIdx === curMonth) {
      const att = drillsNeedingAttention(records).find(a => a.label === drill.label)
      return att && att.status === 'overdue' ? 'overdue' : 'due'
    }
    // Future scheduled slot -> planned
    if (year > curYear || (year === curYear && monthIdx > curMonth)) return 'scheduled'
    // Past scheduled slot, not done -> stays blank (can't run a past month's drill now)
    return 'na'
  }

  const cellBg = (st, bandColor) => st === 'planned' ? 'rgba(127,179,232,.35)' : st === 'done' ? '#5fd6a8' : st === 'overdue' ? '#b4313c' : st === 'due' ? '#f2d84e' : st === 'scheduled' ? (bandColor + '2e') : 'transparent'
  const cellFg = (st, bandColor) => st === 'planned' ? 'var(--text)' : st === 'done' ? '#0f2e20' : st === 'overdue' ? '#3a1414' : st === 'due' ? '#4a3d1c' : st === 'scheduled' ? bandColor : 'var(--muted)'

  // annual drills: count done in the year, show a single summary tick anywhere done
  function annualDone(label) { return doneMonths(label).size > 0 }

  // Per-drill summary rows for the table view
  const att = drillsNeedingAttention(records)
  const attMap = Object.fromEntries(att.map(a => [a.label, a]))
  const drillRows = DRILL_TYPES.map(drill => {
    const mine = (records || []).filter(r => r.drill_type === drill.label && r.drill_date)
    let last = null
    mine.forEach(r => { const d = new Date(r.drill_date + 'T00:00:00'); if (!last || d > last) last = d })
    const a = attMap[drill.label]
    let st = 'ok'
    if (a) st = a.status // 'overdue' | 'due'
    else if (drill.freqMonths === 0) st = 'asreq'
    let nextDue = null
    if (last && drill.freqMonths > 0) { nextDue = new Date(last); nextDue.setMonth(nextDue.getMonth() + drill.freqMonths) }
    return { drill, last, nextDue, status: st, count: mine.length }
  })
  const fmtD = (d) => d ? d.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
  const tabbed = drillRows.filter(r => {
    if (tab === 'overdue' && r.status !== 'overdue') return false
    if (tab === 'due' && r.status !== 'due') return false
    if (tab === 'completed' && !(r.count > 0 && r.status === 'ok')) return false
    if (freqFilter !== 'all' && r.drill.band !== freqFilter) return false
    if (search.trim() && !r.drill.label.toLowerCase().includes(search.trim().toLowerCase())) return false
    return true
  })
  const cnt = (s) => drillRows.filter(r => s === 'completed' ? (r.count > 0 && r.status === 'ok') : r.status === s).length
  // a drill type with a linked Contingency Plan: that plan in drill mode (the Checklists page resolves it, src/lib/drillPlans.js);
  // without one: the drill report checklist as before
  function runDrill(label) {
    if (label && linkFor(label, plansData.links, plansData.plans).plan) {
      requestDrill({ drill_type: label }); localStorage.setItem('hc_page', 'checklists'); if (onNavigate) onNavigate('checklists'); return
    }
    localStorage.setItem('hc_open_checklist', 'RR-04 Drill Report')
    localStorage.setItem('hc_page', 'checklists')
    if (label) localStorage.setItem('hc_drill_prefill', label)
    if (onNavigate) onNavigate('checklists')
  }
  const modalRecords = recordsFor ? (records || []).filter(r => r.drill_type === recordsFor).sort((a,b) => (a.drill_date < b.drill_date ? 1 : -1)) : []

  return (
    <div style={{ padding:'0 4px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem', flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {onNavigate && <button onClick={() => onNavigate('drill_log')} style={btnGhost}>&larr; Drill Log</button>}
          <h2 style={{ fontSize:'1.3rem', fontWeight:600, color:'var(--text)', margin:0 }}>Drill Planning Matrix</h2>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ display:'flex', gap:2, background:'var(--sea)', borderRadius:8, padding:3 }}>
            <button onClick={() => setView('grid')} style={{ ...segBtn, ...(view==='grid'?segOn:{}) }}>Grid</button>
            <button onClick={() => setView('table')} style={{ ...segBtn, ...(view==='table'?segOn:{}) }}>Table</button>
            <button onClick={() => setView('plans')} style={{ ...segBtn, ...(view==='plans'?segOn:{}) }}>Plans</button>
          </div>
          {view === 'grid' && <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={() => setYear(y => y-1)} style={btnGhost}>&larr;</button>
            <span style={{ fontSize:14, fontWeight:600, color:'var(--text)', minWidth:48, textAlign:'center' }}>{year}</span>
            <button onClick={() => setYear(y => y+1)} style={btnGhost}>&rarr;</button>
          </div>}
        </div>
      </div>

      {loading ? <p style={{ color:'var(--muted)' }}>Loading…</p> : view === 'plans' ? (
        <DrillPlansPanel clientId={config.id} data={plansData} canEdit={!can || can('checklists', 'write')} who={whoOf(config)}
          onRunPlan={p => { requestDrill({ plan_id: p.id, drill_type: drillTypesOf(p, plansData.links, plansData.plans)[0] || null }); localStorage.setItem('hc_page', 'checklists'); if (onNavigate) onNavigate('checklists') }} />
      ) : view === 'grid' ? (<>
        {(() => {
          const att = drillsNeedingAttention(records)
          const over = att.filter(a => a.status === 'overdue')
          const due = att.filter(a => a.status === 'due')
          const plannedBox = (openPlans.length > 0 || canPlan) && (
            <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(127,179,232,.1)', border:'1px solid rgba(127,179,232,.35)', marginBottom:14, fontSize:12.5, color:'var(--text)' }}>
              <b>{openPlans.length ? openPlans.length + ' Planned' : 'Planned'}</b>{openPlans.length ? ': ' + openPlans.map(p => p.drill_type + ' due ' + p.due_date + (p.overdue ? ' (passed - not logged)' : '')).join(', ') : ' - no drill planned.'}
              {canPlan && !planForm && <button onClick={() => setPlanForm({ drill_type: DRILL_TYPES[0].label, due_date: '' })} style={{ marginLeft:10, fontSize:12, background:'none', border:'1px solid var(--border)', borderRadius:7, padding:'3px 10px', color:'var(--text)', cursor:'pointer' }}>Plan a drill</button>}
              {planForm && <span style={{ display:'inline-flex', gap:6, marginLeft:10, flexWrap:'wrap', alignItems:'center' }}>
                <select aria-label="Drill" value={planForm.drill_type} onChange={e => setPlanForm(f => ({ ...f, drill_type: e.target.value }))} style={{ fontSize:12, padding:'3px 6px' }}>{DRILL_TYPES.map(d => <option key={d.key} value={d.label}>{d.label}</option>)}</select>
                <input aria-label="Due date" type="date" min={shipTodayAuto()} value={planForm.due_date} onChange={e => setPlanForm(f => ({ ...f, due_date: e.target.value }))} style={{ fontSize:12, padding:'3px 6px' }} />
                <button onClick={savePlan} style={{ fontSize:12, padding:'3px 10px', cursor:'pointer' }}>Save</button>
                <button onClick={() => { setPlanForm(null); setPlanErr('') }} style={{ fontSize:12, background:'none', border:0, color:'var(--muted)', cursor:'pointer' }}>Cancel</button>
              </span>}
              {planErr && <div style={{ color:'#b4313c', marginTop:4 }}>{planErr}</div>}
            </div>
          )
          if (!over.length && !due.length) return <>{plannedBox}<div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(95,214,168,.1)', border:'1px solid rgba(95,214,168,.35)', color:'var(--text)', fontSize:13, marginBottom:14 }}>All drills up to date.</div></>
          return (<>
            {plannedBox}
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:14 }}>
              {over.length > 0 && <div style={{ flex:'1 1 240px', padding:'11px 14px', borderRadius:10, background:'rgba(224,138,138,.12)', border:'1px solid rgba(224,138,138,.4)' }}><div style={{ fontSize:12, fontWeight:700, color:'#b4313c', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>{over.length} Overdue</div><div style={{ fontSize:12.5, color:'var(--text)', lineHeight:1.5 }}>{over.map(o => o.label).join(', ')}</div></div>}
              {due.length > 0 && <div style={{ flex:'1 1 240px', padding:'11px 14px', borderRadius:10, background:'rgba(242,216,78,.12)', border:'1px solid rgba(242,216,78,.4)' }}><div style={{ fontSize:12, fontWeight:700, color:'#76601f', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>{due.length} Due Now</div><div style={{ fontSize:12.5, color:'var(--text)', lineHeight:1.5 }}>{due.map(o => o.label).join(', ')}</div></div>}
            </div>
          </>)
        })()}
        <div style={{ overflowX:'auto', border:'1px solid var(--border)', borderRadius:12 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:820 }}>
            <thead>
              <tr style={{ background:'var(--card, #1a1f2b)' }}>
                <th style={{ ...th, textAlign:'left', minWidth:220 }}>Drill</th>
                <th style={{ ...th, minWidth:70 }}>Ref</th>
                {MONTHS.map(m => <th key={m} style={{ ...th, width:44 }}>{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {DRILL_TYPES.map(drill => {
                const doneSet = doneMonths(drill.label)
                const band = DRILL_BANDS[drill.band]
                return (
                  <tr key={drill.key} style={{ borderTop:'1px solid var(--border)' }}>
                    <td style={{ ...td, textAlign:'left' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ width:8, height:8, borderRadius:2, background:band.color, flexShrink:0 }} />
                        <span style={{ fontSize:12.5, color:'var(--text)' }}>{drill.label}{drill.recommended ? ' *' : ''}</span>
                        <DocChips docs={sopLinks[drill.key]} />
                      </div>
                    </td>
                    <td style={{ ...td, fontSize:10.5, color:'var(--muted)' }}>{drill.sms_ref || '—'}</td>
                    {MONTHS.map((m, mi) => {
                      const st = cellStatus(drill, mi, doneSet)
                      return (
                        <td key={mi} style={{ ...td, padding:3 }}>
                          <div title={st === 'planned' ? 'Planned: due ' + planIn(drill.label, mi).due_date + ' (not held yet)' : undefined}
                            onClick={(e) => { e.stopPropagation(); const rect = e.currentTarget.getBoundingClientRect(); setCellMenu({ label: drill.label, count: doneSet.size, x: rect.left, y: rect.bottom + 6 }) }}
                            style={{ height:26, borderRadius:5, background:cellBg(st, band.color), color:cellFg(st, band.color), display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, cursor:'pointer', border: st === 'scheduled' ? '1px solid ' + band.color + '66' : '1px solid transparent' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)' }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = st === 'scheduled' ? band.color + '66' : 'transparent' }}
                          >
                            {st === 'done' ? '\u2713' : st === 'overdue' ? '!' : st === 'planned' ? 'P' : st === 'scheduled' ? '\u00b7' : ''}
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </>) : (
        <div>
          <div style={{ display:'flex', gap:10, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search drills…" style={{ flex:'1 1 200px', minWidth:160, background:'var(--bg, #0f131a)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 11px', fontSize:13, color:'var(--text)' }} />
            <select value={freqFilter} onChange={e => setFreqFilter(e.target.value)} style={{ background:'var(--bg, #0f131a)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 11px', fontSize:13, color:'var(--text)' }}>
              <option value="all">All frequencies</option>
              {Object.entries(DRILL_BANDS).map(([k,b]) => <option key={k} value={k}>{b.label}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
            {[['all','All'],['overdue','Overdue'],['due','Due'],['completed','Completed']].map(([k,lbl]) => (
              <button key={k} onClick={() => setTab(k)} style={{ ...tabBtn, ...(tab===k?tabOn:{}) }}>{lbl} <span style={{ opacity:.6 }}>{k==='all'?drillRows.length:cnt(k)}</span></button>
            ))}
          </div>
          <div style={{ border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--card, #1a1f2b)' }}>
                <th style={{ ...th, textAlign:'left', paddingLeft:16 }}>Drill</th>
                <th style={{ ...th, textAlign:'left' }}>Frequency</th>
                <th style={{ ...th, textAlign:'left' }}>Last Done</th>
                <th style={{ ...th, textAlign:'left' }}>Next Due</th>
                <th style={{ ...th, textAlign:'left' }}>Status</th>
                <th style={{ ...th, textAlign:'right', paddingRight:16 }}>Actions</th>
              </tr></thead>
              <tbody>
                {tabbed.map(r => {
                  const band = DRILL_BANDS[r.drill.band]
                  const pill = r.status==='overdue' ? {bg:'rgba(224,138,138,.18)',c:'#b4313c',t:'Overdue'} : r.status==='due' ? {bg:'rgba(242,216,78,.18)',c:'#c9a84c',t:'Due'} : r.status==='asreq' ? {bg:'rgba(200,184,232,.16)',c:'#b7a8dc',t:'As required'} : {bg:'rgba(95,214,168,.16)',c:'#5fd6a8',t:'Up to date'}
                  return (
                    <tr key={r.drill.key} style={{ borderTop:'1px solid var(--border)' }}>
                      <td style={{ ...tdL, paddingLeft:16 }}><div style={{ display:'flex', alignItems:'center', gap:8 }}><span style={{ width:8, height:8, borderRadius:2, background:band.color, flexShrink:0 }} /><span style={{ color:'var(--text)' }}>{r.drill.label}{r.drill.recommended?' *':''}{(() => { const pl = linkFor(r.drill.label, plansData.links, plansData.plans).plan; return pl ? <span style={{ display:'block', fontSize:11.5, color:'var(--muted)' }}>Plan: {pl.title}</span> : null })()}</span></div></td>
                      <td style={tdL}>{band.label}</td>
                      <td style={tdL}>{fmtD(r.last)}</td>
                      <td style={tdL}>{r.drill.freqMonths===0 ? '—' : fmtD(r.nextDue)}</td>
                      <td style={tdL}><span style={{ fontSize:11.5, fontWeight:600, padding:'3px 10px', borderRadius:12, background:pill.bg, color:pill.c }}>{pill.t}</span></td>
                      <td style={{ ...tdL, textAlign:'right', paddingRight:16, whiteSpace:'nowrap' }}>
                        {r.count > 0 && <button onClick={() => setRecordsFor(r.drill.label)} style={rowBtnGhost}>View</button>}
                        <button onClick={() => runDrill(r.drill.label)} style={(r.status === 'overdue' || r.status === 'due') ? rowBtnGold : rowBtnQuiet}>{(r.status === 'overdue' || r.status === 'due') ? 'Run drill' : 'Log drill'}</button>
                      </td>
                    </tr>
                  )
                })}
                {tabbed.length === 0 && <tr><td colSpan={6} style={{ ...tdL, paddingLeft:16, color:'var(--muted)' }}>Nothing in this view.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:18, flexWrap:'wrap', marginTop:16, fontSize:12, color:'var(--muted)' }}>
        <Legend c="#5fd6a8" t="Completed" />
        <Legend c="#f2d84e" t="Due this month" />
        <Legend c="#b4313c" t="Overdue" />
        <Legend c="#5b6878" t="Scheduled ahead" />
        <Legend c="#7fb3e8" t="P = planned (due date set, not held yet)" />
        <span style={{ fontStyle:'italic' }}>* strongly recommended, not mandatory</span>
      </div>
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginTop:10, fontSize:11, color:'var(--muted)' }}>
        {Object.values(DRILL_BANDS).map(b => <span key={b.label} style={{ display:'flex', alignItems:'center', gap:6 }}><span style={{ width:8, height:8, borderRadius:2, background:b.color }} />{b.label}</span>)}
      </div>

      {cellMenu && (
        <>
          <div onClick={() => setCellMenu(null)} style={{ position:'fixed', inset:0, zIndex:900 }} />
          <div style={{ position:'fixed', left: Math.min(cellMenu.x, window.innerWidth - 200), top: cellMenu.y, zIndex:901, background:'var(--card, #1a1f2b)', border:'1px solid var(--border)', borderRadius:10, padding:8, minWidth:180, boxShadow:'0 12px 32px rgba(0,0,0,.45)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.04em', padding:'4px 8px 8px' }}>{cellMenu.label}</div>
            <button onClick={() => { const l = cellMenu.label; setCellMenu(null); runDrill(l) }} style={popBtn}>Run drill</button>
            {cellMenu.count > 0 && <button onClick={() => { const l = cellMenu.label; setCellMenu(null); setRecordsFor(l) }} style={popBtn}>View records</button>}
            {cellMenu.count === 0 && <div style={{ fontSize:12, color:'var(--muted)', padding:'6px 8px' }}>No records yet</div>}
          </div>
        </>
      )}

      {recordsFor && (
        <div onClick={() => setRecordsFor(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'var(--card, #1a1f2b)', border:'1px solid var(--border)', borderRadius:14, padding:24, width:'100%', maxWidth:520, maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <h3 style={{ fontSize:'1.05rem', fontWeight:600, color:'var(--text)', margin:0 }}>{recordsFor} — records</h3>
              <button onClick={() => setRecordsFor(null)} style={{ background:'transparent', border:'none', color:'var(--muted)', fontSize:20, cursor:'pointer' }}>×</button>
            </div>
            {modalRecords.length === 0 ? <p style={{ color:'var(--muted)', fontSize:13 }}>No records yet.</p> : modalRecords.map((r, i) => (
              <div key={i} style={{ padding:'10px 0', borderBottom: i < modalRecords.length-1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{fmtD(new Date(r.drill_date + 'T00:00:00'))}</span>
                  {r.pdf_url && <a href={fileHref(r.pdf_url)} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'#2a6a96', textDecoration:'none' }}>PDF</a>}
                </div>
                {r.crew_present && <div style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>Crew: {r.crew_present}</div>}
                {r.notes && <div style={{ fontSize:12, color:'var(--text)', opacity:.85, marginTop:2 }}>{r.notes}</div>}
              </div>
            ))}
            <div style={{ marginTop:16, textAlign:'right' }}>
              <button onClick={() => runDrill(recordsFor)} style={rowBtnGold}>Run new drill</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Legend({ c, t }) {
  return <span style={{ display:'flex', alignItems:'center', gap:6 }}><span style={{ width:14, height:14, borderRadius:4, background:c }} />{t}</span>
}

const th = { padding:'10px 8px', fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.03em', textAlign:'center' }
const td = { padding:'8px', fontSize:12, color:'var(--text)', textAlign:'center', verticalAlign:'middle' }
const btnGhost = { background:'transparent', color:'var(--muted)', border:'none', borderRadius:8, padding:'6px 10px', fontSize:13, cursor:'pointer' }
const tdL = { padding:'11px 12px', fontSize:12.5, color:'var(--muted)', textAlign:'left', verticalAlign:'middle' }
const segBtn = { background:'transparent', color:'var(--muted)', border:'none', borderRadius:6, padding:'5px 14px', fontSize:12.5, fontWeight:600, cursor:'pointer' }
const segOn = { background:'var(--gold)', color:'#1a1a1a' }
const tabBtn = { background:'transparent', color:'var(--muted)', border:'1px solid var(--border)', borderRadius:16, padding:'6px 14px', fontSize:12.5, fontWeight:500, cursor:'pointer' }
const tabOn = { background:'rgba(184,150,62,.15)', color:'var(--gold2)', borderColor:'var(--gold)' }
const rowBtnGold = { background:'var(--gold)', color:'#1a1a1a', border:'none', borderRadius:7, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer', marginLeft:8 }
const rowBtnGhost = { background:'transparent', color:'var(--muted)', border:'1px solid var(--border)', borderRadius:7, padding:'5px 12px', fontSize:12, cursor:'pointer' }
const rowBtnQuiet = { background:'transparent', color:'var(--muted)', border:'1px solid var(--border)', borderRadius:7, padding:'5px 12px', fontSize:12, cursor:'pointer', marginLeft:8 }
const popBtn = { display:'block', width:'100%', textAlign:'left', background:'transparent', color:'var(--text)', border:'none', borderRadius:7, padding:'8px 8px', fontSize:13, cursor:'pointer' }
